import styled, { css } from "styled-components";

const prose = css`
  p {
    color: rgba(226, 232, 240, 0.92);
    font-size: clamp(0.95rem, 1.05vw, 1.05rem);
    line-height: 1.65;
    margin: 0 0 1rem;
  }

  b,
  strong {
    color: #c7d2fe;
    font-weight: 650;
  }

  a {
    color: #7dd3fc;
    text-decoration: none;
    border-bottom: 1px solid rgba(125, 211, 252, 0.35);
    transition: color 0.2s ease, border-color 0.2s ease;

    &:hover {
      color: #a5b4fc;
      border-bottom-color: rgba(165, 180, 252, 0.6);
    }
  }
`;

const railWidth = "min(28vw, 300px)";

/** Full-page shell: poster rail + main. With `$splitScroll`, rail is fixed and only `WorkMain` scrolls. */
export const WorkShell = styled.div<{ $splitScroll?: boolean }>`
  display: flex;
  flex: 1;
  min-height: 0;
  background: radial-gradient(ellipse 120% 80% at 50% -20%, rgba(79, 70, 229, 0.12), transparent 50%),
    #020617;
  color: #e2e8f0;
  ${(p) =>
    p.$splitScroll
      ? css`
          overflow: hidden;
        `
      : css`
          overflow: auto;
        `}
  ${prose}
`;

export const WorkSideRail = styled.aside<{
  $splitScroll?: boolean;
  $railVariant?: "default" | "editorial";
}>`
  display: none;

  @media (min-width: 576px) {
    display: flex;
    flex-direction: column;
    width: ${railWidth};
    flex-shrink: 0;
    min-height: 0;
    border-right: 1px solid rgba(148, 163, 184, 0.14);
    background: linear-gradient(165deg, #0f172a 0%, #020617 55%, #020617 100%);
    box-shadow: inset -1px 0 0 rgba(99, 102, 241, 0.06);

    ${(p) =>
      p.$splitScroll
        ? css`
            position: fixed;
            left: 0;
            top: 4.625rem;
            bottom: 0;
            width: ${railWidth};
            z-index: 2;
            height: auto;
          `
        : css`
            position: sticky;
            top: 0;
            align-self: flex-start;
            height: 100vh;
          `}

    ${(p) =>
      p.$railVariant === "editorial"
        ? css`
            background:
              radial-gradient(ellipse 90% 50% at 30% 20%, rgba(99, 102, 241, 0.14), transparent 55%),
              radial-gradient(ellipse 70% 40% at 80% 90%, rgba(34, 211, 238, 0.08), transparent 50%),
              linear-gradient(165deg, #0c1222 0%, #1e1b4b 38%, #0f172a 100%);
            border-right: 1px solid rgba(129, 140, 248, 0.22);
            box-shadow:
              inset -16px 0 48px rgba(0, 0, 0, 0.4),
              inset 0 0 0 1px rgba(255, 255, 255, 0.03);
          `
        : undefined}
  }
`;

/** Poster in the rail: full-height cover. Editorial adds vignette to match gradient rail. */
export const WorkRailPoster = styled.div<{ $variant?: "default" | "editorial" }>`
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;

  ${(p) =>
    p.$variant === "editorial"
      ? css`
          display: block;

          &::after {
            content: "";
            position: absolute;
            inset: 0;
            pointer-events: none;
            z-index: 1;
            background: linear-gradient(
              180deg,
              rgba(15, 23, 42, 0.45) 0%,
              transparent 28%,
              transparent 62%,
              rgba(15, 23, 42, 0.55) 100%
            );
            opacity: 0.85;
          }

          &::before {
            content: "";
            position: absolute;
            inset: 0;
            pointer-events: none;
            z-index: 1;
            background: linear-gradient(
              90deg,
              rgba(99, 102, 241, 0.12) 0%,
              transparent 35%
            );
          }

          img {
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center;
            z-index: 0;
            opacity: 1;
          }
        `
      : css`
          display: flex;
          align-items: stretch;
          justify-content: stretch;
          padding: 0;

          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center;
            opacity: 0.95;
          }
        `}
`;

export const WorkMain = styled.div<{ $splitScroll?: boolean }>`
  flex: 1;
  min-width: 0;
  min-height: 0;
  ${(p) =>
    p.$splitScroll
      ? css`
          overflow-x: hidden;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;

          @media (min-width: 576px) {
            margin-left: ${railWidth};
          }
        `
      : css`
          overflow: auto;
        `}
`;

export const WorkHero = styled.header`
  text-align: center;
  padding: clamp(2rem, 5vw, 3.25rem) clamp(1rem, 4vw, 2rem) clamp(1.5rem, 4vw, 2.5rem);
  position: relative;

  &::after {
    content: "";
    position: absolute;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    width: min(420px, 70%);
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(129, 140, 248, 0.5), transparent);
  }
`;

export const WorkHeroEyebrow = styled.p`
  margin: 0 0 0.65rem !important;
  font-size: 0.72rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #22d3ee !important;
  opacity: 0.95;
`;

export const WorkHeroTitle = styled.h1`
  margin: 0 auto;
  max-width: 18ch;
  font-size: clamp(1.65rem, 3.8vw, 2.65rem);
  font-weight: 750;
  line-height: 1.15;
  letter-spacing: -0.02em;
  background: linear-gradient(135deg, #f8fafc 0%, #e0e7ff 45%, #a5b4fc 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  text-wrap: balance;
`;

export const WorkHeroLead = styled.div`
  margin: 1.35rem auto 0;
  max-width: 52rem;
  text-align: center;

  p {
    margin-bottom: 0 !important;
    font-size: clamp(1rem, 1.15vw, 1.12rem);
    line-height: 1.7;
    color: rgba(203, 213, 225, 0.95) !important;
  }
`;

export const WorkContentContainer = styled.div`
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 clamp(1rem, 3vw, 1.75rem) clamp(2.5rem, 6vw, 4rem);
`;

export const WorkSplitRow = styled.div<{ $reverse?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: clamp(1.25rem, 3vw, 2.25rem);
  margin-bottom: clamp(2rem, 4.5vw, 3rem);

  & > * {
    flex: 1 1 280px;
    min-width: 0;
  }

  @media (min-width: 768px) {
    flex-direction: ${(p) => (p.$reverse ? "row-reverse" : "row")};
  }
`;

export const WorkMediaFrame = styled.div`
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(129, 140, 248, 0.22);
  background: rgba(15, 23, 42, 0.85);
  box-shadow:
    0 24px 48px rgba(0, 0, 0, 0.45),
    0 0 0 1px rgba(0, 0, 0, 0.2);

  video,
  img {
    display: block;
    width: 100%;
    height: auto;
    vertical-align: middle;
  }
`;

export const WorkTextStack = styled.div`
  padding: 0.25rem 0;

  h2:not(:first-child) {
    margin-top: 1.35rem;
  }
`;

export const WorkSectionHeading = styled.h2`
  margin: 0 0 0.75rem;
  font-size: clamp(1.05rem, 1.35vw, 1.2rem);
  font-weight: 700;
  color: #e0e7ff;
  letter-spacing: -0.01em;
  line-height: 1.3;

  &::after {
    content: "";
    display: block;
    width: 2.5rem;
    height: 3px;
    margin-top: 0.5rem;
    border-radius: 2px;
    background: linear-gradient(90deg, #6366f1, #22d3ee);
  }
`;

export const StyledImageGrid = styled.img`
  border-radius: 14px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.4);
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.35s ease;

  &:hover {
    transform: scale(1.015) translateY(-2px);
    box-shadow: 0 20px 50px rgba(99, 102, 241, 0.18);
  }
`;

/** @deprecated Use WorkShell — kept for gradual migration */
export const DetailsPageStyles = WorkShell;

export const ConcludeContainer = styled.section`
  margin: clamp(1.5rem, 4vw, 2.5rem) 0;
  padding: clamp(1.5rem, 3vw, 2.25rem) clamp(1.25rem, 3vw, 2rem);
  border-radius: 16px;
  background: linear-gradient(145deg, rgba(15, 23, 42, 0.92) 0%, rgba(30, 27, 75, 0.35) 100%);
  border: 1px solid rgba(129, 140, 248, 0.2);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.35);

  p {
    color: rgba(226, 232, 240, 0.95) !important;
    text-align: center;
    max-width: 52rem;
    margin-left: auto;
    margin-right: auto;
  }
`;

export const WorkGallerySection = styled.section`
  padding: clamp(1.5rem, 4vw, 2.5rem) 0 clamp(2rem, 5vw, 3rem);
  max-width: 1120px;
  margin: 0 auto;

  .gallery-custom-wrapper {
    gap: 0.75rem;
  }

  .gallery-item img {
    border-radius: 12px;
    border: 1px solid rgba(148, 163, 184, 0.2);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
  }

  .gallery-item:hover img {
    transform: translateY(-3px);
    box-shadow: 0 12px 28px rgba(99, 102, 241, 0.2);
  }
`;

/** Full-width narrative block (e.g. no media column). */
export const WorkProseBlock = styled.div`
  margin-bottom: clamp(1.5rem, 3vw, 2.25rem);
  flex: 1 1 100%;
`;

export const WorkReferencesHeading = styled.h3`
  margin: 0 0 1rem;
  font-size: 1.05rem;
  font-weight: 700;
  color: #f1f5f9;
  text-align: center;
`;

export const WorkReferenceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  max-width: 36rem;
  margin: 0 auto;
`;

export const WorkExternalLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  border: 1px solid rgba(129, 140, 248, 0.35);
  background: rgba(15, 23, 42, 0.75);
  color: #e2e8f0 !important;
  font-size: 0.95rem;
  font-weight: 500;
  border-bottom: none !important;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;

  &:hover {
    background: rgba(99, 102, 241, 0.22);
    border-color: rgba(165, 180, 252, 0.55);
    transform: translateY(-1px);
  }
`;

export const WorkHighlightBand = styled.div`
  margin: clamp(1.5rem, 4vw, 2.5rem) 0;
  padding: clamp(1.25rem, 3vw, 1.75rem);
  border-radius: 14px;
  text-align: center;
  background: linear-gradient(
    135deg,
    rgba(251, 191, 36, 0.08) 0%,
    rgba(99, 102, 241, 0.12) 100%
  );
  border: 1px solid rgba(251, 191, 36, 0.2);

  p {
    margin: 0 !important;
    color: rgba(254, 243, 199, 0.95) !important;
    font-size: clamp(0.98rem, 1.1vw, 1.08rem);
    line-height: 1.65;
    max-width: 52rem;
    margin-inline: auto !important;
  }
`;
